import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import AttachmentsWidget from '../components/AttachmentsWidget';

// Constants
const CP_TYPES = [
  { value: 'buyer', label: 'Buyer', color: '#22c55e' },
  { value: 'seller', label: 'Seller', color: '#3b82f6' },
  { value: 'broker', label: 'Broker', color: '#f59e0b' },
  { value: 'bank', label: 'Bank', color: '#8b5cf6' },
  { value: 'inspector', label: 'Inspector', color: '#06b6d4' },
  { value: 'other', label: 'Sonstige', color: '#6b7280' },
];

const KYC_STATUS = {
  pending:     { label: 'Ausstehend',    color: '#6b7280', bg: '#6b728020' },
  in_progress: { label: 'In Bearbeitung', color: '#f59e0b', bg: '#f59e0b20' },
  approved:    { label: 'Genehmigt',     color: '#22c55e', bg: '#22c55e20' },
  rejected:    { label: 'Abgelehnt',     color: '#ef4444', bg: '#ef444420' },
  expired:     { label: 'Abgelaufen',    color: '#f97316', bg: '#f9731620' },
};

const RISK_LEVELS = {
  low:       { label: 'Niedrig',    color: '#22c55e' },
  medium:    { label: 'Mittel',     color: '#f59e0b' },
  high:      { label: 'Hoch',       color: '#f97316' },
  very_high: { label: 'Sehr hoch',  color: '#ef4444' },
};

const DOC_TYPES = [
  { value: 'corporate_registry', label: 'Handelsregister' },
  { value: 'bank_reference',     label: 'Bankreferenz' },
  { value: 'tax_cert',           label: 'Steuerbescheinigung' },
  { value: 'id_document',        label: 'ID / Passport' },
  { value: 'kyc_form',           label: 'KYC-Formular' },
  { value: 'aml_form',           label: 'AML-Formular' },
  { value: 'other',              label: 'Sonstiges' },
];

const DOC_STATUS = {
  missing:  { label: 'Fehlt',        color: '#ef4444' },
  pending:  { label: 'Ausstehend',   color: '#6b7280' },
  received: { label: 'Eingegangen',  color: '#f59e0b' },
  verified: { label: 'Verifiziert',  color: '#22c55e' },
  expired:  { label: 'Abgelaufen',   color: '#f97316' },
};

function TypeBadge({ type }) {
  const m = CP_TYPES.find(t => t.value === type) || CP_TYPES[CP_TYPES.length - 1];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
      borderRadius: 4, fontSize: 11, fontWeight: 600,
      background: m.color + '20', color: m.color }}>{m.label}</span>
  );
}

function KYCBadge({ status }) {
  const m = KYC_STATUS[status] || KYC_STATUS.pending;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
      borderRadius: 4, fontSize: 11, fontWeight: 600,
      background: m.bg, color: m.color }}>{m.label}</span>
  );
}

// ── Create/Edit Modal ────────────────────────────────────────────────────────
function CPModal({ cp, onClose, onSaved }) {
  const empty = {
    company_name: '', cp_type: 'buyer', trading_name: '', country: '', city: '',
    registration_number: '', tax_id: '', website: '',
    contact_name: '', contact_email: '', contact_phone: '', contact_title: '',
    kyc_status: 'pending', kyc_date: '', kyc_expiry: '', kyc_notes: '',
    risk_level: 'medium', sanctions_checked: false, sanctions_result: 'not_checked',
    notes: '', tags: [],
  };
  const [form, setForm] = useState(cp ? { ...empty, ...cp } : empty);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      if (cp) {
        await API.put(`/counterparty/${cp.id}`, form);
      } else {
        await API.post('/counterparty/', form);
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setLoading(false); }
  };

  const inp = (label, key, type = 'text', required = false) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)',
        marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {label}{required && ' *'}
      </label>
      <input type={type} value={form[key] || ''} required={required}
        onChange={e => set(key, e.target.value)}
        style={{ width: '100%', padding: '7px 11px', borderRadius: 6,
          background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 13 }} />
    </div>
  );

  const sel = (label, key, options) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)',
        marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</label>
      <select value={form[key]} onChange={e => set(key, e.target.value)}
        style={{ width: '100%', padding: '7px 11px', borderRadius: 6,
          background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 13 }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#00000090',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12,
        padding: 24, width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>
          {cp ? 'Counterparty bearbeiten' : 'Neue Counterparty'}
        </div>
        {err && <div style={{ padding: '8px 12px', background: '#ef444415', border: '1px solid #ef444440',
          borderRadius: 6, color: '#ef4444', fontSize: 13, marginBottom: 14 }}>{err}</div>}
        <form onSubmit={handle}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {inp('Firmenname', 'company_name', 'text', true)}
            {sel('Typ', 'cp_type', CP_TYPES)}
            {inp('Handelsname', 'trading_name')}
            {inp('Land', 'country')}
            {inp('Stadt', 'city')}
            {inp('Handelsregisternummer', 'registration_number')}
            {inp('Steuer-ID', 'tax_id')}
            {inp('Website', 'website')}
          </div>

          <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0 14px', paddingTop: 14,
            fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Hauptkontakt
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {inp('Name', 'contact_name')}
            {inp('Position', 'contact_title')}
            {inp('E-Mail', 'contact_email', 'email')}
            {inp('Telefon', 'contact_phone', 'tel')}
          </div>

          <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0 14px', paddingTop: 14,
            fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            KYC & Risiko
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            {sel('KYC-Status', 'kyc_status', Object.entries(KYC_STATUS).map(([v, m]) => ({ value: v, label: m.label })))}
            {inp('KYC-Datum', 'kyc_date', 'date')}
            {inp('KYC-Ablauf', 'kyc_expiry', 'date')}
            {sel('Risikolevel', 'risk_level', Object.entries(RISK_LEVELS).map(([v, m]) => ({ value: v, label: m.label })))}
            {sel('Sanctions Check', 'sanctions_result', [
              { value: 'not_checked', label: 'Nicht geprüft' },
              { value: 'clear', label: 'Keine Treffer' },
              { value: 'flagged', label: 'Auffällig' },
            ])}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)',
                marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>Sanctions geprüft</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text1)', paddingTop: 8 }}>
                <input type="checkbox" checked={form.sanctions_checked}
                  onChange={e => set('sanctions_checked', e.target.checked)} />
                Ja
              </label>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)',
              marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>Notizen</label>
            <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={3}
              style={{ width: '100%', padding: '7px 11px', borderRadius: 6,
                background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)',
                fontSize: 13, resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" onClick={onClose}
              style={{ padding: '8px 18px', background: 'transparent', color: 'var(--text2)',
                border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
              Abbrechen
            </button>
            <button type="submit" disabled={loading}
              style={{ padding: '8px 18px', background: '#22c55e', color: '#000',
                border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {loading ? '...' : cp ? 'Speichern' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Detail View ──────────────────────────────────────────────────────────────
function CPDetail({ cp: initialCp, onBack, onUpdated }) {
  const [cp, setCp] = useState(initialCp);
  const [tab, setTab] = useState('info');
  const [contacts, setContacts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [newContact, setNewContact] = useState(null);
  const [newDoc, setNewDoc] = useState(null);
  const [err, setErr] = useState('');

  const loadContacts = useCallback(async () => {
    try {
      const r = await API.get(`/counterparty/${cp.id}/contacts`);
      setContacts(r.data);
    } catch { setErr('Fehler beim Laden der Kontakte'); }
  }, [cp.id]);

  const loadDocs = useCallback(async () => {
    try {
      const r = await API.get(`/counterparty/${cp.id}/documents`);
      setDocs(r.data);
    } catch { setErr('Fehler beim Laden der Dokumente'); }
  }, [cp.id]);

  const reloadCp = useCallback(async () => {
    try {
      const r = await API.get(`/counterparty/${cp.id}`);
      setCp(r.data);
      onUpdated();
    } catch { }
  }, [cp.id, onUpdated]);

  useEffect(() => {
    if (tab === 'contacts') loadContacts();
    if (tab === 'documents') loadDocs();
  }, [tab, loadContacts, loadDocs]);

  const TABS = [
    { id: 'info', label: 'Info' },
    { id: 'contacts', label: 'Kontakte' },
    { id: 'documents', label: 'Dokumente' },
    { id: 'kyc', label: 'KYC / AML' },
    { id: 'attachments', label: 'Anhänge' },
  ];

  const addContact = async (data) => {
    await API.post(`/counterparty/${cp.id}/contacts`, data);
    setNewContact(null);
    loadContacts();
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Kontakt löschen?')) return;
    await API.delete(`/counterparty/${cp.id}/contacts/${id}`);
    loadContacts();
  };

  const addDoc = async (data) => {
    await API.post(`/counterparty/${cp.id}/documents`, data);
    setNewDoc(null);
    loadDocs();
  };

  const updateDocStatus = async (doc, status) => {
    await API.put(`/counterparty/${cp.id}/documents/${doc.id}`, { ...doc, status });
    loadDocs();
  };

  return (
    <div>
      {editOpen && <CPModal cp={cp} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); reloadCp(); }} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={onBack}
          style={{ padding: '6px 12px', background: 'transparent', color: 'var(--text2)',
            border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
          &larr; Zurück
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text1)' }}>{cp.company_name}</h2>
            <TypeBadge type={cp.cp_type} />
            <KYCBadge status={cp.kyc_status} />
          </div>
          {cp.trading_name && <div style={{ fontSize: 12, color: 'var(--text3)' }}>Trading: {cp.trading_name}</div>}
        </div>
        <button onClick={() => setEditOpen(true)}
          style={{ padding: '7px 14px', background: '#22c55e15', color: '#22c55e',
            border: '1px solid #22c55e30', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          Bearbeiten
        </button>
      </div>

      {err && <div style={{ padding: '8px 12px', background: '#ef444415', border: '1px solid #ef444440',
        borderRadius: 6, color: '#ef4444', fontSize: 13, marginBottom: 14 }}>{err}</div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 16px', background: 'none', border: 'none',
            borderBottom: tab === t.id ? '2px solid #22c55e' : '2px solid transparent',
            color: tab === t.id ? '#22c55e' : 'var(--text2)',
            fontWeight: tab === t.id ? 600 : 400, cursor: 'pointer', fontSize: 13,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Info Tab */}
      {tab === 'info' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
              letterSpacing: '.06em', marginBottom: 14 }}>Firmendaten</div>
            {[
              ['Land', cp.country],
              ['Stadt', cp.city],
              ['Registernummer', cp.registration_number],
              ['Steuer-ID', cp.tax_id],
              ['Website', cp.website],
            ].map(([label, val]) => val && (
              <div key={label} style={{ display: 'flex', gap: 8, padding: '6px 0',
                borderBottom: '1px solid var(--bg-hover)', fontSize: 13 }}>
                <span style={{ color: 'var(--text3)', minWidth: 130 }}>{label}</span>
                <span style={{ color: 'var(--text1)' }}>{val}</span>
              </div>
            ))}
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
              letterSpacing: '.06em', marginBottom: 14 }}>Hauptkontakt</div>
            {[
              ['Name', cp.contact_name],
              ['Position', cp.contact_title],
              ['E-Mail', cp.contact_email],
              ['Telefon', cp.contact_phone],
            ].map(([label, val]) => val && (
              <div key={label} style={{ display: 'flex', gap: 8, padding: '6px 0',
                borderBottom: '1px solid var(--bg-hover)', fontSize: 13 }}>
                <span style={{ color: 'var(--text3)', minWidth: 100 }}>{label}</span>
                <span style={{ color: 'var(--text1)' }}>{val}</span>
              </div>
            ))}
          </div>
          {cp.notes && (
            <div style={{ gridColumn: '1 / -1', background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
                letterSpacing: '.06em', marginBottom: 8 }}>Notizen</div>
              <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>{cp.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Contacts Tab */}
      {tab === 'contacts' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
            <button onClick={() => setNewContact({})}
              style={{ padding: '7px 14px', background: '#22c55e', color: '#000',
                border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              + Kontakt hinzufügen
            </button>
          </div>
          {newContact !== null && (
            <div style={{ background: 'var(--bg-card)', border: '1px solid #22c55e40', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Neuer Kontakt</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[['Name *', 'name', 'text'], ['Position', 'title', 'text'], ['E-Mail', 'email', 'email'], ['Telefon', 'phone', 'tel']].map(([l, k, t]) => (
                  <div key={k}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--text2)', marginBottom: 3 }}>{l}</label>
                    <input type={t} value={newContact[k] || ''}
                      onChange={e => setNewContact(c => ({ ...c, [k]: e.target.value }))}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: 6,
                        background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 12 }} />
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                <button onClick={() => setNewContact(null)}
                  style={{ padding: '6px 14px', background: 'transparent', color: 'var(--text2)',
                    border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                  Abbrechen
                </button>
                <button onClick={() => addContact(newContact)} disabled={!newContact.name}
                  style={{ padding: '6px 14px', background: '#22c55e', color: '#000',
                    border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                  Hinzufügen
                </button>
              </div>
            </div>
          )}
          {contacts.length === 0 && !newContact && (
            <div style={{ color: 'var(--text3)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
              Keine Kontakte. Füge den ersten hinzu.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {contacts.map(c => (
              <div key={c.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {c.name}
                    {c.is_primary && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px',
                      background: '#22c55e20', color: '#22c55e', borderRadius: 3 }}>Haupt</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                    {[c.title, c.email, c.phone].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button onClick={() => deleteContact(c.id)}
                  style={{ padding: '4px 10px', background: '#ef444415', color: '#ef4444',
                    border: '1px solid #ef444430', borderRadius: 5, cursor: 'pointer', fontSize: 11 }}>
                  Löschen
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {tab === 'documents' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
            <button onClick={() => setNewDoc({})}
              style={{ padding: '7px 14px', background: '#22c55e', color: '#000',
                border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              + Dokument hinzufügen
            </button>
          </div>
          {newDoc !== null && (
            <div style={{ background: 'var(--bg-card)', border: '1px solid #22c55e40', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Neues Dokument</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text2)', marginBottom: 3 }}>Typ</label>
                  <select value={newDoc.doc_type || 'other'} onChange={e => setNewDoc(d => ({ ...d, doc_type: e.target.value }))}
                    style={{ width: '100%', padding: '6px 10px', borderRadius: 6,
                      background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 12 }}>
                    {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '2 / -1' }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text2)', marginBottom: 3 }}>Bezeichnung *</label>
                  <input value={newDoc.title || ''} onChange={e => setNewDoc(d => ({ ...d, title: e.target.value }))}
                    style={{ width: '100%', padding: '6px 10px', borderRadius: 6,
                      background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 12 }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text2)', marginBottom: 3 }}>Ausgestellt</label>
                  <input type="date" value={newDoc.issue_date || ''} onChange={e => setNewDoc(d => ({ ...d, issue_date: e.target.value }))}
                    style={{ width: '100%', padding: '6px 10px', borderRadius: 6,
                      background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 12 }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text2)', marginBottom: 3 }}>Ablauf</label>
                  <input type="date" value={newDoc.expiry_date || ''} onChange={e => setNewDoc(d => ({ ...d, expiry_date: e.target.value }))}
                    style={{ width: '100%', padding: '6px 10px', borderRadius: 6,
                      background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 12 }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setNewDoc(null)}
                  style={{ padding: '6px 14px', background: 'transparent', color: 'var(--text2)',
                    border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                  Abbrechen
                </button>
                <button onClick={() => addDoc({ ...newDoc, doc_type: newDoc.doc_type || 'other', status: 'pending' })} disabled={!newDoc.title}
                  style={{ padding: '6px 14px', background: '#22c55e', color: '#000',
                    border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                  Hinzufügen
                </button>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {docs.map(doc => {
              const sm = DOC_STATUS[doc.status] || DOC_STATUS.missing;
              const dt = DOC_TYPES.find(t => t.value === doc.doc_type)?.label || doc.doc_type;
              return (
                <div key={doc.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 8, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{doc.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                      {dt}
                      {doc.expiry_date && ` · Ablauf: ${doc.expiry_date}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <select value={doc.status} onChange={e => updateDocStatus(doc, e.target.value)}
                      style={{ padding: '4px 8px', borderRadius: 5, fontSize: 12,
                        background: sm.color + '15', border: `1px solid ${sm.color}40`,
                        color: sm.color, cursor: 'pointer' }}>
                      {Object.entries(DOC_STATUS).map(([v, m]) => (
                        <option key={v} value={v} style={{ background: 'var(--bg-card)', color: 'var(--text1)' }}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}
            {docs.length === 0 && !newDoc && (
              <div style={{ color: 'var(--text3)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
                Keine Dokumente. Füge das erste hinzu.
              </div>
            )}
          </div>
        </div>
      )}

      {/* KYC Tab */}
      {tab === 'kyc' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
              letterSpacing: '.06em', marginBottom: 14 }}>KYC-Status</div>
            {[
              ['Status', <KYCBadge key="kyc" status={cp.kyc_status} />],
              ['KYC-Datum', cp.kyc_date || '—'],
              ['Ablauf', cp.kyc_expiry || '—'],
              ['Risikolevel', <span key="risk" style={{ color: RISK_LEVELS[cp.risk_level]?.color }}>{RISK_LEVELS[cp.risk_level]?.label || cp.risk_level}</span>],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', gap: 8, padding: '8px 0',
                borderBottom: '1px solid var(--bg-hover)', fontSize: 13, alignItems: 'center' }}>
                <span style={{ color: 'var(--text3)', minWidth: 130 }}>{label}</span>
                <span>{val}</span>
              </div>
            ))}
            {cp.kyc_notes && (
              <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>NOTIZEN</div>
                {cp.kyc_notes}
              </div>
            )}
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
              letterSpacing: '.06em', marginBottom: 14 }}>Sanctions Screening</div>
            {[
              ['Geprüft', cp.sanctions_checked ? 'Ja' : 'Nein'],
              ['Prüfdatum', cp.sanctions_date || '—'],
              ['Ergebnis', <span key="result" style={{ color: cp.sanctions_result === 'clear' ? '#22c55e' : cp.sanctions_result === 'flagged' ? '#ef4444' : '#6b7280' }}>
                {cp.sanctions_result === 'clear' ? 'Keine Treffer' : cp.sanctions_result === 'flagged' ? 'Auffällig' : 'Nicht geprüft'}
              </span>],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', gap: 8, padding: '8px 0',
                borderBottom: '1px solid var(--bg-hover)', fontSize: 13, alignItems: 'center' }}>
                <span style={{ color: 'var(--text3)', minWidth: 130 }}>{label}</span>
                <span>{val}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'attachments' && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
          <AttachmentsWidget entityType="counterparty" entityId={cp.id} />
        </div>
      )}
    </div>
  );
}

// ── List View ─────────────────────────────────────────────────────────────────
export default function CounterpartyPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterKyc, setFilterKyc] = useState('');
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (filterType) params.cp_type = filterType;
      if (filterKyc) params.kyc_status = filterKyc;
      const r = await API.get('/counterparty/', { params });
      setItems(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler beim Laden');
    } finally { setLoading(false); }
  }, [search, filterType, filterKyc]);

  useEffect(() => { load(); }, [load]);

  if (selected) {
    return <CPDetail cp={selected} onBack={() => setSelected(null)} onUpdated={load} />;
  }

  const kycCounts = Object.entries(KYC_STATUS).map(([k, m]) => ({
    key: k, label: m.label, color: m.color, count: items.filter(i => i.kyc_status === k).length,
  })).filter(x => x.count > 0);

  return (
    <div>
      {showCreate && <CPModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text1)' }}>Counterparties</h1>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>
            Buyer, Seller, Broker &amp; weitere Geschäftspartner — inkl. KYC-Status
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          style={{ padding: '8px 16px', background: '#22c55e', color: '#000',
            border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          + Neue Counterparty
        </button>
      </div>

      {err && <div style={{ padding: '8px 12px', background: '#ef444415', border: '1px solid #ef444440',
        borderRadius: 6, color: '#ef4444', fontSize: 13, marginBottom: 14 }}>{err}</div>}

      {/* KYC Summary */}
      {kycCounts.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {kycCounts.map(x => (
            <div key={x.key} onClick={() => setFilterKyc(filterKyc === x.key ? '' : x.key)}
              style={{ padding: '8px 14px', background: x.color + '15', border: `1px solid ${x.color}40`,
                borderRadius: 8, cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center',
                opacity: filterKyc && filterKyc !== x.key ? 0.4 : 1 }}>
              <span style={{ fontSize: 18, fontWeight: 800, color: x.color }}>{x.count}</span>
              <span style={{ fontSize: 12, color: x.color }}>{x.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suche..."
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 7,
            background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 13 }} />
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 7,
            background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 13 }}>
          <option value="">Alle Typen</option>
          {CP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={filterKyc} onChange={e => setFilterKyc(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 7,
            background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 13 }}>
          <option value="">Alle KYC-Status</option>
          {Object.entries(KYC_STATUS).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ color: 'var(--text3)', fontSize: 13, padding: 20, textAlign: 'center' }}>Lade...</div>
      ) : items.length === 0 ? (
        <div style={{ color: 'var(--text3)', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>
          {search || filterType || filterKyc ? 'Keine Treffer für diese Filter.' : 'Noch keine Counterparties. Erstelle die erste.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Unternehmen', 'Typ', 'Land', 'Hauptkontakt', 'KYC', 'Risiko', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11,
                    fontWeight: 700, color: 'var(--text3)', borderBottom: '1px solid var(--border)',
                    textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(item)}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #1a2a1a' }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text1)' }}>{item.company_name}</div>
                    {item.trading_name && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{item.trading_name}</div>}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #1a2a1a' }}>
                    <TypeBadge type={item.cp_type} />
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #1a2a1a', fontSize: 13, color: 'var(--text2)' }}>
                    {item.country || '—'}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #1a2a1a', fontSize: 13, color: 'var(--text2)' }}>
                    <div>{item.contact_name || '—'}</div>
                    {item.contact_email && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{item.contact_email}</div>}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #1a2a1a' }}>
                    <KYCBadge status={item.kyc_status} />
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #1a2a1a' }}>
                    <span style={{ fontSize: 12, color: RISK_LEVELS[item.risk_level]?.color }}>
                      {RISK_LEVELS[item.risk_level]?.label || item.risk_level}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #1a2a1a' }}>
                    <span style={{ fontSize: 12, color: '#22c55e' }}>Details &rarr;</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
